import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import type { TagEntry } from '@/features/gate/commands'
import { newId } from '@/lib/ids'
import { enqueueCommand } from '@/offline'
import { useToast } from '@/ui'
import {
  Btn,
  Cta,
  Footer,
  ItemRow,
  Keypad,
  Readout,
  Row,
  V1Screen,
  V1Sheet,
  WaitCta,
} from '@/features/gate/v1/kit'
import { V1Photo } from '@/features/gate/v1/photo'
import { LiveScanner, Refused, TypeCodeSheet } from '@/features/gate/v1/scan'
import { C, typeKey } from '@/features/gate/v1/style'
import {
  cropName,
  cropWord,
  fmtWhen,
  kg,
  sackName,
  shortCode,
  shortLot,
  tr,
  plain,
} from '@/features/gate/v1/text'
import { kgWire, type CountBayPayload, type ReplaceContainerPayload } from '../commands'
import {
  bagsAt,
  placePath,
  type BagView,
  type LocalReplacement,
  type StoreState,
} from '../projection'
import {
  candidateMatch,
  checkOldTag,
  checkReplacementTag,
  classifyScan,
  compareCount,
} from '../rules'
import { useRefusalText } from '../components/useRefusalText'
import { useDraftState, useNow } from '../useStore'
import { DoneScreen, Hint, IconBox, Saved, StoreHeader, Tally, WithStore, agoText } from './bits'
import { SIcon, type StoreIconName } from './icons'
import { PlacePicker } from './PlacePicker'
import { TagScanner } from './TagScanner'

interface Counted {
  count_id: string
  location_id: string
  command_id: string
}

/**
 * V1 — फटी बोरी बदलें (F5-B1.a on the phone): 1) count the place where the bag was found
 * (count_bay), 2) a new sack with a new tag from the keeper's own roll, weighed, photographed →
 * replace_container, which depends on the count. Same rules as the full screen (ReplacePage):
 * the supervisor decides b) and c) in the console; this phone shows each replacement's state and,
 * for an unproven (ordinary orphan) sack, the red-tie and move jobs.
 */
export function V1Replace() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [counted, setCounted] = useState<Counted | null>(null)
  const home = () => navigate('/m/store')
  return (
    <WithStore onBack={home}>
      {(state) => {
        const loc = counted?.location_id ?? params.get('location')
        const local = loc ? state.counts.get(loc) : undefined
        const count =
          counted ??
          (local && (!params.get('count') || params.get('count') === local.count_id)
            ? {
                count_id: local.count_id,
                location_id: local.location_id,
                command_id: local.command_id,
              }
            : null) ??
          // The count's own link carries it all: the phone's list may have been downloaded since,
          // which drops the count from `state.counts`.
          (loc && params.get('count') && params.get('cmd')
            ? {
                count_id: params.get('count')!,
                location_id: loc,
                command_id: params.get('cmd')!,
              }
            : null)
        return count ? (
          <ReplaceForm
            key={count.count_id}
            state={state}
            count={count}
            tripId={params.get('trip')}
            fresh={counted !== null}
            onHome={home}
          />
        ) : (
          <CountStep state={state} initialLocationId={loc} onCounted={setCounted} onHome={home} />
        )
      }}
    </WithStore>
  )
}

// ---------------------------------------------------------------------------------------------
// Step 1: count the place (as CountFlow)
// ---------------------------------------------------------------------------------------------

interface CountDraft {
  count_id: string
  location_id: string | null
  started_at: string | null
  codes: string[]
}

const emptyCount = (locationId: string | null): CountDraft => ({
  count_id: newId(),
  location_id: locationId,
  started_at: null,
  codes: [],
})

function CountStep({
  state,
  initialLocationId,
  onCounted,
  onHome,
}: {
  state: StoreState
  initialLocationId: string | null
  onCounted: (r: Counted) => void
  onHome: () => void
}) {
  const toast = useToast()
  const actor = useActor()
  const now = useNow()
  const refusal = useRefusalText()
  // The same draft as the full count screen's CountFlow (one count of a place at a time).
  const [draft, update, loaded, clear] = useDraftState<CountDraft>(
    `store.count:${state.siteId}`,
    'store.count',
    () => emptyCount(initialLocationId),
    actor.user_id,
  )
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState<string | null>(null)

  const location = draft.location_id ? (state.locations.get(draft.location_id) ?? null) : null
  const expected = useMemo(() => (location ? bagsAt(state, location.id) : []), [state, location])
  const cmp = useMemo(
    () => compareCount(state, expected, draft.codes),
    [state, expected, draft.codes],
  )
  const requests = state.cache.count_requests.filter((r) => !state.counts.has(r.location_id))
  const placeCodes = useMemo(() => [...state.locationByCode.keys()], [state.locationByCode])
  const bagCodes = useMemo(() => [...state.bagByPayload.keys()], [state.bagByPayload])

  const onPlace = (code: string): string | null => {
    const k = classifyScan(state, code)
    if (k.kind !== 'LOCATION') return refusal('TAG_WRONG_KIND')
    if (!k.location.active) return refusal('LOCATION_INACTIVE')
    update((d) => ({
      ...d,
      location_id: k.location.id,
      codes: [],
      started_at: new Date().toISOString(),
    }))
    return null
  }

  const onTag = (code: string): string | null => {
    if (draft.codes.includes(code)) return refusal('DUPLICATE')
    update((d) => (d.codes.includes(code) ? d : { ...d, codes: [...d.codes, code] }))
    setSaved(code)
    return null
  }

  const done = async () => {
    if (!location) return
    setSaving(true)
    try {
      const payload: CountBayPayload = {
        count: {
          id: draft.count_id,
          location_id: location.id,
          qr_payloads: draft.codes,
          started_at: draft.started_at ?? new Date().toISOString(),
          ended_at: new Date().toISOString(),
        },
      }
      const parents = [
        ...new Set(expected.map((b) => b.pending_command_id).filter((x): x is string => !!x)),
      ]
      const rec = await enqueueCommand({
        type: 'count_bay',
        payload,
        actorId: actor.user_id,
        parentIds: parents,
      })
      const result = { count_id: draft.count_id, location_id: location.id, command_id: rec.id }
      await clear()
      onCounted(result)
    } catch (e) {
      toast.error(tr('store.v1.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  if (!loaded) return <V1Screen />

  const header = (
    <StoreHeader onBack={onHome} icon="problembag" title={tr('store.v1.replace.title')} step={1} />
  )

  // Which place?
  if (!location)
    return (
      <V1Screen
        header={header}
        footer={
          <Footer>
            <WaitCta>{tr('store.v1.put.scanPlace')}</WaitCta>
          </Footer>
        }
      >
        <Hint icon="tag" text={tr('store.v1.replace.countFirst')} />
        {requests.map((r) => {
          const l = state.locations.get(r.location_id)
          return (
            <ItemRow
              key={r.id}
              padding="8px 10px"
              border={C.acc}
              borderWidth={2}
              onClick={() =>
                update((d) => ({
                  ...d,
                  location_id: r.location_id,
                  started_at: new Date().toISOString(),
                }))
              }
              lead={<IconBox icon="ticks" size={44} iconSize={26} />}
              title={l ? (l.name ?? l.code) : r.location_id}
              line={
                <span style={{ color: C.muted }}>
                  {tr('store.v1.replace.asked', { ago: agoText(r.requested_at, now) })}
                  {r.note ? ` · ${r.note}` : ''}
                </span>
              }
              right={
                <span style={{ color: C.acc, display: 'flex' }}>
                  <SIcon name="next" size={24} sw={2.5} />
                </span>
              }
            />
          )
        })}
        <TagScanner
          hint={tr('store.v1.put.scanPlace')}
          payloads={placeCodes}
          onCode={onPlace}
          kind="place"
        />
        <PlacePicker
          state={state}
          onPick={(id) => {
            const l = state.locations.get(id)
            if (l) onPlace(l.code)
          }}
        />
        <Recent state={state} />
      </V1Screen>
    )

  // Count every tag there.
  const odd = cmp.extra.length + cmp.unknown.length
  return (
    <V1Screen
      header={header}
      footer={
        <Footer>
          <Row>
            {draft.codes.length === 0 ? (
              <Btn
                icon="back"
                onClick={() =>
                  update((d) => ({ ...d, location_id: null, codes: [], started_at: null }))
                }
              >
                {tr('store.v1.put.change')}
              </Btn>
            ) : (
              <Btn
                icon="undo"
                onClick={() => {
                  setSaved(null)
                  update((d) => ({ ...d, codes: d.codes.slice(0, -1) }))
                }}
              >
                {tr('store.v1.put.removeLast')}
              </Btn>
            )}
          </Row>
          <Cta
            label={tr('store.v1.replace.countDone')}
            icon="next"
            busy={saving}
            onClick={() => void done()}
          />
        </Footer>
      }
    >
      {saved && <Saved text={tr('store.v1.tag', { tag: shortCode(saved) })} />}
      <ItemRow
        lead={<IconBox icon="godown" size={44} iconSize={26} />}
        title={location.name ?? location.code}
        line={<span style={{ color: C.muted }}>{placePath(state, location.id)}</span>}
        right={
          <Tally
            value={`${cmp.seen.length}/${cmp.expected.length}`}
            label={tr('store.v1.replace.counted')}
          />
        }
      />
      <Hint icon="tag" text={tr('store.v1.replace.countEvery')} />
      <TagScanner
        hint={tr(draft.codes.length ? 'store.v1.scanOn.hold' : 'store.v1.scanOff.first')}
        payloads={bagCodes}
        onCode={onTag}
      />
      {cmp.byLot.map((l) => {
        const lot = state.lots.get(l.unit_id)
        const red = lot?.stream === 'CONV'
        const short = l.short > 0 && draft.codes.length > 0
        return (
          <ItemRow
            key={l.unit_id}
            padding="6px 10px"
            border={short ? C.acc : C.rule}
            lead={
              <span style={{ color: red ? C.ink : C.ok, display: 'flex' }}>
                <SIcon name={red ? 'redtie' : 'sack'} size={26} />
              </span>
            }
            title={tr('store.v1.replace.lotLine', {
              lot: lot ? shortLot(lot.code) : '—',
              n: l.seen,
              m: l.expected,
            })}
            line={
              short ? (
                <b style={{ color: C.accDark }}>
                  {tr('store.v1.replace.short', { count: l.short })}
                </b>
              ) : undefined
            }
            right={l.short === 0 ? <SIcon name="ok" size={22} sw={3} /> : undefined}
          />
        )
      })}
      {cmp.byLot.length === 0 && <Hint icon="sack" text={tr('store.v1.replace.nothingHere')} />}
      {odd > 0 && (
        <Hint
          icon="bang"
          tone="warn"
          text={tr('store.v1.replace.notFromHere', {
            count: odd,
            tags: [...cmp.extra.map((b) => b.qr_payload), ...cmp.unknown].map(shortCode).join(', '),
          })}
        />
      )}
    </V1Screen>
  )
}

/** Replacements from this phone in the last 14 days and what happened to each. */
function Recent({ state }: { state: StoreState }) {
  const navigate = useNavigate()
  const now = useNow()
  const recent = state.replacements.filter((r) => now - r.created_at < 14 * 86_400_000)
  if (recent.length === 0) return null
  const convBay = [...state.locations.values()].find((l) => l.role === 'CONVENTIONAL')
  const step = (r: LocalReplacement): { icon: StoreIconName; color: string; text: string } => {
    if (r.status === 'ERROR')
      return { icon: 'bang', color: C.bad, text: tr('store.v1.replace.statusError') }
    if (!['APPLIED', 'DUPLICATE', 'HELD'].includes(r.status))
      return { icon: 'cloudup', color: C.muted, text: tr('store.v1.replace.statusPending') }
    if (r.evidence === 'NONE')
      return { icon: 'redtie', color: C.bad, text: tr('store.v1.replace.statusOrphan') }
    return { icon: 'hourglass', color: C.accDark, text: tr('store.v1.replace.statusReview') }
  }
  return (
    <>
      <b style={{ fontSize: 18, marginTop: 6 }}>{tr('store.v1.replace.recent')}</b>
      {recent.map((r) => {
        const s = step(r)
        return (
          <section
            key={r.command_id}
            style={{
              background: C.surface,
              border: `1.5px solid ${C.rule}`,
              borderRadius: 12,
              padding: '8px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: s.color, display: 'flex' }}>
                <SIcon name={s.icon} size={28} />
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
                <b style={{ fontSize: 17 }}>
                  {tr('store.v1.replace.recentLine', {
                    old: r.payload.old_qr_payload
                      ? shortCode(r.payload.old_qr_payload)
                      : tr('store.v1.replace.lost'),
                    new: shortCode(r.payload.new_container.qr_payload),
                  })}
                </b>
                <span style={{ fontSize: 15, color: C.muted }}>{fmtWhen(r.created_at, now)}</span>
              </span>
            </div>
            <span style={{ fontSize: 16, lineHeight: 1.35 }}>{s.text}</span>
            {r.evidence === 'NONE' && (
              <Row>
                <Btn icon="redtie" onClick={() => navigate('/m/store/red-tie')}>
                  {tr('store.v1.more.redTie')}
                </Btn>
                {convBay && (
                  <Btn
                    icon="rowbags"
                    onClick={() => navigate(`/m/store/move?to=${encodeURIComponent(convBay.id)}`)}
                  >
                    {tr('store.v1.more.move')}
                  </Btn>
                )}
              </Row>
            )}
          </section>
        )
      })}
    </>
  )
}

// ---------------------------------------------------------------------------------------------
// Step 2: the new sack
// ---------------------------------------------------------------------------------------------

interface ReplaceDraft {
  reason: 'TORN' | 'TAG_LOST'
  old_qr: string | null
  new_qr: string | null
  sack_type_id: string | null
  value_g: number | null
  photo_id: string | null
  crop_id: string | null
}

const emptyReplace = (): ReplaceDraft => ({
  reason: 'TORN',
  old_qr: null,
  new_qr: null,
  sack_type_id: null,
  value_g: null,
  photo_id: null,
  crop_id: null,
})

type Panel = 'old' | 'new' | 'weight' | 'sack' | 'crop' | 'photo' | null

/**
 * A bare number typed from a new tag: expanded with the prefix of the keeper's replacement roll
 * that holds it (as the gate does for its rolls). Anything else is returned upper-cased.
 */
function expandRollCode(
  rolls: ReadonlyArray<{
    kind: string
    payload_prefix: string
    start_sequence: number
    end_sequence: number
    issued_to_id?: string | null
    [k: string]: unknown
  }>,
  raw: string,
  actorId: string,
): string {
  const c = raw.trim().toUpperCase()
  if (!/^\d+$/.test(c)) return c
  const n = Number(c)
  const roll = rolls.find(
    (r) =>
      r.kind === 'BAG' &&
      (!r.issued_to_id || r.issued_to_id === actorId) &&
      n >= r.start_sequence &&
      n <= r.end_sequence,
  )
  if (!roll) return c
  const listed = Array.isArray(roll.payloads) ? roll.payloads[n - roll.start_sequence] : undefined
  if (typeof listed === 'string' && listed) return listed.toUpperCase()
  const digits = typeof roll.sequence_digits === 'number' ? roll.sequence_digits : 0
  return `${roll.payload_prefix}${String(n).padStart(digits, '0')}`.toUpperCase()
}

function ReplaceForm({
  state,
  count,
  tripId,
  fresh,
  onHome,
}: {
  state: StoreState
  count: Counted
  tripId: string | null
  /** The count was just sent from this screen. */
  fresh: boolean
  onHome: () => void
}) {
  const toast = useToast()
  const actor = useActor()
  const refusal = useRefusalText()
  const [draft, update, loaded, clear] = useDraftState<ReplaceDraft>(
    `store.v1.replace:${count.count_id}`,
    'store.v1.replace',
    emptyReplace,
    actor.user_id,
  )
  const [saving, setSaving] = useState(false)
  const [panel, setPanel] = useState<Panel>(null)
  const [done, setDone] = useState<string | null>(null)
  const location = state.locations.get(count.location_id)
  const expected = useMemo(() => bagsAt(state, count.location_id), [state, count.location_id])
  const localCount = state.counts.get(count.location_id)
  // The count's tags are on this phone only until the next download of the list; without them
  // nothing is known short here (and no bag is offered as a candidate).
  const cmp = useMemo(
    () => (localCount ? compareCount(state, expected, localCount.qr_payloads) : null),
    [state, expected, localCount],
  )
  // A tagless bag on a short trip: the trip's missing / unscanned bags are candidates too.
  const tripCandidates: BagView[] = useMemo(() => {
    const trip = tripId ? state.trips.get(tripId) : undefined
    if (!trip) return []
    return trip.items
      .map((i) => (i.container_id ? state.bags.get(i.container_id) : undefined))
      .filter((b): b is BagView => !!b && (b.status === 'MISSING' || b.status === 'IN_TRANSIT'))
  }, [state, tripId])
  const unseen = [...(cmp?.unseen ?? []), ...tripCandidates]

  const old = draft.old_qr ? state.bagByPayload.get(draft.old_qr) : undefined
  const sack = state.cache.sack_types.find((s) => s.id === draft.sack_type_id)
  const newNet = draft.value_g !== null && sack ? draft.value_g - sack.tare_g : null
  const tolerance = state.cache.settings.replace_match_tolerance_g
  const match = !old && newNet !== null ? candidateMatch(unseen, newNet, tolerance) : null
  const crops = [
    ...new Set(
      expected.map((b) => state.lots.get(b.unit_id)?.crop_id).filter((c): c is string => !!c),
    ),
  ]
  const needCrop = !old && crops.length > 1
  const ready =
    draft.new_qr &&
    draft.sack_type_id &&
    draft.value_g &&
    draft.value_g > 0 &&
    draft.photo_id &&
    (!needCrop || draft.crop_id)

  const cropText = (c: string) => {
    const w = cropName([], c)
    if (w !== c) return w
    const name = [...state.lots.values()].find((l) => l.crop_id === c)?.crop_name
    return name ? cropWord(name) : c
  }

  const replace = async () => {
    if (!ready || !location) return
    setSaving(true)
    try {
      const payload: ReplaceContainerPayload = {
        count_id: count.count_id,
        location_id: location.id,
        reason: draft.old_qr ? draft.reason : 'TAG_LOST',
        old_qr_payload: draft.old_qr,
        new_container: {
          id: newId(),
          qr_payload: draft.new_qr!,
          sack_type_id: draft.sack_type_id!,
        },
        measurement: {
          id: newId(),
          value: kgWire(draft.value_g!),
          value_g: draft.value_g!,
          uom: 'KG',
          method: 'MANUAL_ENTRY',
          taken_at: new Date().toISOString(),
        },
        photo_id: draft.photo_id!,
        crop_id: needCrop ? draft.crop_id : crops.length === 1 ? (crops[0] ?? null) : null,
      }
      await enqueueCommand({
        type: 'replace_container',
        payload,
        actorId: actor.user_id,
        // depends_on the count (F5-B1.a: a count of the place is submitted first).
        parentIds: [count.command_id],
        mediaIds: [draft.photo_id!],
      })
      setDone(payload.new_container.qr_payload)
      await clear()
    } catch (e) {
      toast.error(tr('store.v1.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  if (done)
    return (
      <DoneScreen
        title={tr('store.v1.replace.doneTitle')}
        lines={tr('store.v1.replace.doneLine', { tag: shortCode(done) })}
        chips={[{ icon: 'hourglass', text: tr('store.v1.replace.doneChip') }]}
        onHome={onHome}
      />
    )
  if (!loaded || !location) return <V1Screen />

  const back = () => setPanel(null)
  const header = (
    <StoreHeader onBack={onHome} icon="problembag" title={tr('store.v1.replace.title')} step={2} />
  )

  if (panel === 'old')
    return (
      <ScanPanel
        header={header}
        hint={tr('store.v1.replace.oldHint')}
        onBack={back}
        scanner={
          <TagScanner
            hint={plain(tr('store.v1.replace.oldHint'))}
            payloads={[...state.bagByPayload.keys()]}
            onCode={(c) => {
              const v = checkOldTag(state, c, expected)
              if (!v.ok) return refusal(v.reason === 'DUPLICATE' ? 'TAG_ALREADY_USED' : v.reason)
              update((d) => ({ ...d, old_qr: c, reason: 'TORN' }))
              back()
              return null
            }}
          />
        }
      />
    )
  if (panel === 'new')
    return (
      <ScanPanel
        header={header}
        hint={tr('store.v1.replace.newHint')}
        onBack={back}
        scanner={
          <NewTagScanner
            hint={plain(tr('store.v1.replace.newHint'))}
            check={(raw, entry) => {
              const c =
                entry === 'TYPED'
                  ? expandRollCode(state.cache.rolls, raw, actor.user_id)
                  : raw.trim().toUpperCase()
              const r = checkReplacementTag(state, c, actor.user_id)
              if (r) return refusal(r)
              update((d) => ({ ...d, new_qr: c }))
              back()
              return null
            }}
          />
        }
      />
    )
  if (panel === 'weight')
    return (
      <WeightPanel
        header={header}
        initial={draft.value_g}
        tare={sack?.tare_g ?? null}
        onBack={back}
        onSave={(g) => {
          update((d) => ({ ...d, value_g: g }))
          back()
        }}
      />
    )

  const missing: Array<{ text: string; panel: Panel }> = [
    ...(!draft.new_qr ? [{ text: tr('store.v1.replace.waitNew'), panel: 'new' as const }] : []),
    ...(!draft.sack_type_id
      ? [{ text: tr('store.v1.replace.waitSack'), panel: 'sack' as const }]
      : []),
    ...(!draft.value_g
      ? [{ text: tr('store.v1.replace.waitWeight'), panel: 'weight' as const }]
      : []),
    ...(!draft.photo_id
      ? [{ text: tr('store.v1.replace.waitPhoto'), panel: 'photo' as const }]
      : []),
    ...(needCrop && !draft.crop_id
      ? [{ text: tr('store.v1.replace.waitCrop'), panel: 'crop' as const }]
      : []),
  ]
  const shortLots = cmp ? cmp.byLot.filter((l) => l.short > 0) : []

  return (
    <V1Screen
      header={header}
      footer={
        <Footer>
          {ready ? (
            <Cta
              label={tr('store.v1.replace.confirm')}
              icon="ok"
              busy={saving}
              onClick={() => void replace()}
            />
          ) : (
            <WaitCta onClick={() => setPanel(missing[0]?.panel ?? null)}>
              {missing[0]?.text}
            </WaitCta>
          )}
        </Footer>
      }
      overlay={
        panel === 'sack' ? (
          <V1Sheet title={tr('store.v1.replace.sack')} onClose={back} top={200}>
            {state.cache.sack_types.map((s) => (
              <ItemRow
                key={s.id}
                onClick={() => {
                  update((d) => ({ ...d, sack_type_id: s.id }))
                  back()
                }}
                padding="10px 12px"
                border={s.id === draft.sack_type_id ? C.ink : C.border}
                borderWidth={s.id === draft.sack_type_id ? 3 : 1.5}
                lead={<SIcon name="sack" size={28} />}
                title={sackName(state.cache.sack_types, s.id)}
                line={
                  <span style={{ color: C.muted }}>
                    {tr('store.v1.replace.sackWeight', { kg: kg(s.tare_g) })}
                  </span>
                }
              />
            ))}
          </V1Sheet>
        ) : panel === 'crop' ? (
          <V1Sheet title={tr('store.v1.replace.crop')} onClose={back} top={260}>
            {crops.map((c) => (
              <ItemRow
                key={c}
                onClick={() => {
                  update((d) => ({ ...d, crop_id: c }))
                  back()
                }}
                padding="10px 12px"
                border={c === draft.crop_id ? C.ink : C.border}
                borderWidth={c === draft.crop_id ? 3 : 1.5}
                title={cropText(c)}
              />
            ))}
          </V1Sheet>
        ) : undefined
      }
    >
      {fresh && <Saved text={tr('store.v1.replace.countSaved')} />}
      <ItemRow
        lead={<IconBox icon="godown" size={44} iconSize={26} />}
        title={location.name ?? location.code}
        line={
          shortLots.length > 0 ? (
            <b style={{ color: C.accDark }}>
              {shortLots
                .map((l) =>
                  tr('store.v1.replace.lotShort', {
                    lot: shortLot(state.lots.get(l.unit_id)?.code ?? '—'),
                    count: l.short,
                  }),
                )
                .join(' · ')}
            </b>
          ) : (
            <span style={{ color: C.muted }}>{placePath(state, location.id)}</span>
          )
        }
      />
      {unseen.length === 0 && (
        <Hint icon="redtie" tone="warn" text={tr('store.v1.replace.noShort')} />
      )}

      <PartRow
        n={1}
        title={tr('store.v1.replace.old')}
        value={draft.old_qr ? tr('store.v1.tag', { tag: shortCode(draft.old_qr) }) : null}
        empty={tr('store.v1.replace.oldNone')}
        action={
          draft.old_qr
            ? {
                icon: 'x',
                label: tr('store.v1.replace.remove'),
                onClick: () => update((d) => ({ ...d, old_qr: null, reason: 'TAG_LOST' })),
              }
            : { icon: 'scan', label: tr('store.v1.replace.scan'), onClick: () => setPanel('old') }
        }
      />
      {!draft.old_qr && (
        <Hint icon="supervisor" tone="warn" text={tr('store.v1.replace.callSupervisor')} />
      )}
      <PartRow
        n={2}
        title={tr('store.v1.replace.new')}
        value={draft.new_qr ? tr('store.v1.tag', { tag: shortCode(draft.new_qr) }) : null}
        empty={tr('store.v1.replace.newEmpty')}
        action={{
          icon: 'scan',
          label: tr(draft.new_qr ? 'store.v1.form.again' : 'store.v1.replace.scan'),
          onClick: () => setPanel('new'),
        }}
      />
      <PartRow
        n={3}
        title={tr('store.v1.replace.sack')}
        value={sack ? sackName(state.cache.sack_types, sack.id) : null}
        empty={tr('store.v1.replace.pick')}
        action={{
          icon: 'next',
          label: tr(sack ? 'store.v1.replace.change' : 'store.v1.replace.pickBtn'),
          onClick: () => setPanel('sack'),
        }}
      />
      <PartRow
        n={4}
        title={tr('store.v1.replace.weight')}
        value={draft.value_g ? tr('store.v1.stock.kg', { kg: kg(draft.value_g) }) : null}
        empty={tr('store.v1.replace.weightEmpty')}
        action={{
          icon: 'keypad',
          label: tr(draft.value_g ? 'store.v1.replace.change' : 'store.v1.replace.type'),
          onClick: () => setPanel('weight'),
        }}
      />
      {newNet !== null && (
        <section
          style={{
            background: C.surface,
            border: `1.5px solid ${C.rule}`,
            borderRadius: 12,
            padding: '8px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            fontSize: 17,
          }}
        >
          <span>
            {tr('store.v1.replace.net')}{' '}
            <b style={{ fontSize: 22 }}>{tr('store.v1.stock.kg', { kg: kg(newNet) })}</b>
          </span>
          {old && <span>{tr('store.v1.replace.gateNet', { kg: kg(old.net_g_gate ?? 0) })}</span>}
          {old &&
            old.net_g_current !== null &&
            old.net_g_current !== undefined &&
            newNet > old.net_g_current + tolerance && (
              <b style={{ color: C.bad }}>{tr('store.v1.replace.heavier')}</b>
            )}
          {match && (
            <b style={{ color: match.candidate ? C.ok : C.accDark }}>
              {match.candidate
                ? tr('store.v1.replace.candidate', {
                    tag: shortCode(match.candidate.qr_payload),
                    lot: shortLot(state.lots.get(match.candidate.unit_id)?.code ?? ''),
                    kg: kg(match.candidate.net_g_current ?? match.candidate.net_g_gate ?? 0),
                  })
                : tr('store.v1.replace.noCandidate', { count: match.matches.length })}
            </b>
          )}
        </section>
      )}
      <V1Photo
        value={draft.photo_id}
        onChange={(id) => update((d) => ({ ...d, photo_id: id }))}
        open={panel === 'photo'}
        onOpenChange={(o) => setPanel(o ? 'photo' : null)}
        render={({ preview, busy, take }) => (
          <ItemRow
            padding="8px 10px"
            lead={
              preview ? (
                <img
                  src={preview}
                  alt=""
                  style={{ width: 52, height: 52, borderRadius: 10, objectFit: 'cover' }}
                />
              ) : (
                <Num n={5} />
              )
            }
            title={tr('store.v1.replace.photo')}
            line={
              <span style={{ color: draft.photo_id ? C.ok : C.muted }}>
                {tr(draft.photo_id ? 'store.v1.form.taken' : 'store.v1.replace.photoEmpty')}
              </span>
            }
            right={
              <div style={{ flex: '0 0 auto', display: 'flex' }}>
                <Btn icon="camera" disabled={busy} onClick={take}>
                  {tr(draft.photo_id ? 'store.v1.form.again' : 'store.v1.form.take')}
                </Btn>
              </div>
            }
          />
        )}
      />
      {needCrop && (
        <PartRow
          n={6}
          title={tr('store.v1.replace.crop')}
          value={draft.crop_id ? cropText(draft.crop_id) : null}
          empty={tr('store.v1.replace.pick')}
          action={{
            icon: 'next',
            label: tr(draft.crop_id ? 'store.v1.replace.change' : 'store.v1.replace.pickBtn'),
            onClick: () => setPanel('crop'),
          }}
        />
      )}
      <Hint icon="scissors" text={tr('store.v1.replace.cutOld')} />
    </V1Screen>
  )
}

/** The number in a ring at the front of a part row. */
function Num({ n }: { n: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 36,
        height: 36,
        flexShrink: 0,
        borderRadius: 18,
        border: `2px solid ${C.ink}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        fontWeight: 700,
        boxSizing: 'border-box',
      }}
    >
      {n}
    </span>
  )
}

/** One part of the new sack: number, what it is, what was given (or what is missing), a button. */
function PartRow({
  n,
  title,
  value,
  empty,
  action,
}: {
  n: number
  title: string
  value: string | null
  empty: string
  action: { icon: 'scan' | 'x' | 'next' | 'keypad'; label: string; onClick: () => void }
}) {
  return (
    <ItemRow
      padding="8px 10px"
      border={value ? C.ok : C.rule}
      lead={
        value ? (
          <span
            aria-hidden="true"
            style={{
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: 18,
              background: C.ok,
              color: '#ffffff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SIcon name="ok" size={22} sw={3} />
          </span>
        ) : (
          <Num n={n} />
        )
      }
      title={title}
      line={
        value ? (
          <b style={{ fontSize: 18 }}>{value}</b>
        ) : (
          <span style={{ color: C.muted }}>{empty}</span>
        )
      }
      right={
        <div style={{ flex: '0 0 auto', display: 'flex' }}>
          <Btn icon={action.icon} onClick={action.onClick}>
            {action.label}
          </Btn>
        </div>
      }
    />
  )
}

/** A scanner on its own screen (old tag, new tag), with पीछे at the foot. */
function ScanPanel({
  header,
  hint,
  scanner,
  onBack,
}: {
  header: ReactNode
  hint: string
  scanner: ReactNode
  onBack: () => void
}) {
  return (
    <V1Screen
      header={header}
      footer={
        <Footer>
          <Cta label={tr('gate.v1.back')} icon="back" lead onClick={onBack} />
        </Footer>
      }
    >
      <Hint icon="tag" text={hint} />
      {scanner}
    </V1Screen>
  )
}

/**
 * The new tag's scanner: like TagScanner, but a typed number is a tag of the keeper's own roll,
 * not yet on any list, so the caller expands it.
 */
function NewTagScanner({
  hint,
  check,
}: {
  hint: string
  check: (raw: string, entry: TagEntry) => string | null
}) {
  const [err, setErr] = useState<string | null>(null)
  const [typing, setTyping] = useState(false)
  return (
    <>
      {typing && (
        <TypeCodeSheet
          title={tr('store.v1.typeTitle')}
          check={(raw, entry) => {
            setErr(null)
            return check(raw, entry)
          }}
          onClose={() => setTyping(false)}
        />
      )}
      {!typing && (
        <LiveScanner icon="tag" hint={hint} minHeight={170} check={check} onError={setErr} />
      )}
      <Row>
        <Btn icon="keypad" onClick={() => setTyping(true)}>
          {tr('gate.v1.tag.type')}
        </Btn>
      </Row>
      {err && <Refused text={err} />}
    </>
  )
}

/** The new sack's weight on the keypad (sack included, as the scale shows it). */
function WeightPanel({
  header,
  initial,
  tare,
  onBack,
  onSave,
}: {
  header: ReactNode
  initial: number | null
  tare: number | null
  onBack: () => void
  onSave: (grams: number) => void
}) {
  const [v, setV] = useState(initial ? String(initial / 1000) : '')
  const onKey = useCallback((k: string) => setV((c) => typeKey(c, k, { decimals: 2, max: 6 })), [])
  const grams = Math.round(Number(v) * 1000)
  const ok = v !== '' && Number.isFinite(grams) && grams > 0
  return (
    <V1Screen
      header={header}
      footer={
        <Footer>
          <Row>
            <Btn icon="back" onClick={onBack}>
              {tr('gate.v1.back')}
            </Btn>
          </Row>
          {ok ? (
            <Cta
              label={tr('store.v1.replace.weightSave')}
              icon="ok"
              onClick={() => onSave(grams)}
            />
          ) : (
            <WaitCta>{tr('store.v1.replace.weightEmpty')}</WaitCta>
          )}
        </Footer>
      }
    >
      <Hint icon="scale" text={tr('store.v1.replace.weightHint')} />
      <Readout
        label={tr('store.v1.replace.weight')}
        value={v}
        unit={tr('store.v1.replace.kgUnit')}
        side={
          tare !== null && ok && grams > tare ? (
            <span style={{ fontSize: 15, color: C.muted, textAlign: 'right' }}>
              {tr('store.v1.replace.net')}
              <br />
              <b style={{ fontSize: 20, color: C.ink }}>{kg(grams - tare)}</b>
            </span>
          ) : undefined
        }
      />
      <div style={{ flexGrow: 1 }} />
      <Keypad onKey={onKey} />
    </V1Screen>
  )
}
